import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";

const BkashCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [message, setMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false); // Add processing flag
  const [loading, setLoading] = useState(false);
  const processedPayments = useRef<Set<string>>(new Set()); // Track processed payment IDs
  const [buttonUrl, setButtonUrl] = useState("");

  // Helper function to update submission payment status
  const updateSubmissionPayment = async (
    uniqueId: string,
    paymentData: {
      paymentStatus: string;
      bkashTrxId: string | null;
      bkashPaymentId: string;
    }
  ) => {
    return await axios.put(
      `${process.env.REACT_APP_API_URL}/api/submissions/${uniqueId}/payment`,
      paymentData
    );
  };

  // Helper function to redirect to success page
  const redirectToSuccess = (submissionData: any, customUrl: string) => {
    const successUrl = `/success/${customUrl}?success=true&message=${encodeURIComponent(
      "Form submitted and payment processed successfully!"
    )}&submissionId=${
      submissionData.submissionId || submissionData.id
    }&uniqueId=${submissionData.uniqueId}&editCode=${submissionData.editCode}`;

    setTimeout(() => {
      window.location.href = successUrl;
      console.log("Redirecting to success page:", successUrl);
    }, 2000);
  };

  // Helper function to check payment status on server
  const checkPaymentStatusOnServer = async (paymentID: string) => {
    const serverResponse = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/bkash/submission/${paymentID}`
    );
    return serverResponse;
  };

  // Helper function to handle failed/cancelled payments
  const handleFailedPayment = async (
    paymentID: string,
    submissionData: any,
    failureType: "failed" | "cancelled"
  ) => {
    if (submissionData) {
      try {
        const serverResponse = await checkPaymentStatusOnServer(paymentID);

        if (serverResponse.data.success && serverResponse.data.submission) {
          const serverSubmissionData = serverResponse.data.submission;
          // Check if payment is actually not completed
          if (serverSubmissionData.paymentInfo?.status !== "completed") {
            setStatus("error");
            const action =
              failureType === "failed" ? "failed" : "was cancelled";
            setMessage(
              `Payment ${action}. You can retry payment using this link: ${window.location.origin}/retry-payment/${serverSubmissionData.uniqueId}/${paymentID}`
            );
            setButtonUrl(
              `/retry-payment/${serverSubmissionData.uniqueId}/${paymentID}`
            );
            console.log(
              "Payment was not completed, allowing retry:",
              serverSubmissionData
            );
          } else {
            setStatus("success");
            setMessage("Payment was already completed successfully!");
          }
        } else {
          setStatus("error");
          setMessage(
            failureType === "failed"
              ? "Payment was cancelled or failed"
              : "Payment was cancelled by user"
          );
        }
      } catch (error) {
        setStatus("error");
        setMessage(
          failureType === "failed"
            ? "Payment was cancelled or failed"
            : "Payment was cancelled by user"
        );
      }
    } else {
      setStatus("error");
      setMessage(
        failureType === "failed"
          ? "Payment was cancelled or failed"
          : "Payment was cancelled by user"
      );
    }
  };

  useEffect(() => {
    const processCallback = async () => {
      const paymentID = searchParams.get("paymentID");

      // Prevent duplicate processing using both state and ref
      if (
        isProcessing ||
        !paymentID ||
        processedPayments.current.has(paymentID)
      ) {
        console.log(
          "🔄 Already processing callback or invalid payment ID, skipping..."
        );
        return;
      }

      // Mark this payment as being processed
      processedPayments.current.add(paymentID);
      setIsProcessing(true);

      try {
        const status = searchParams.get("status");

        if (!paymentID) {
          setStatus("error");
          setMessage("Invalid payment callback - missing payment ID");
          setIsProcessing(false);
          return;
        }

        // Get stored payment session data once
        const storedSession = sessionStorage.getItem("bkashPaymentSession");
        let submissionData = null;

        if (storedSession) {
          // Payment session exists, we can use it for additional validation if needed
          submissionData = JSON.parse(storedSession);
          console.log("Payment session found:", submissionData);
        }

        if (status === "success") {
          // Execute the payment
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/bkash/execute`,
            { paymentID }
          );

          if (response.data.success) {
            setStatus("success");
            setMessage("Payment completed successfully!");
            console.log("Payment execution successful:", response.data);
            redirectToSuccess(response.data.submission, response.data.submission?.form?.customUrl);
          } else {
            // Check if payment was already executed
            if (response.data.alreadyExecuted) {
              setStatus("success");
              setMessage("Payment was already completed successfully!");
            } else {
              // Payment execution failed, but we'll still update the submission
              console.warn("bKash execution failed:", response.data.message);
              setStatus("success");
              setMessage("Payment initiated. Submission saved successfully.");
            }
          }

        } else if (status === "failure") {
          // Handle failed payment with server verification
          await handleFailedPayment(paymentID, submissionData, "failed");
        } else if (status === "cancel") {
          // Handle cancelled payment with server verification
          await handleFailedPayment(paymentID, submissionData, "cancelled");
        } else {
          setStatus("error");
          setMessage("Invalid payment status");
        }
      } catch (error: any) {
        console.error("Callback processing error:", error);
        setStatus("error");
        setMessage(
          error.response?.data?.message || "Payment processing failed"
        );
      } finally {
        // Always reset processing flag
        setIsProcessing(false);
      }
    };

    processCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // isProcessing intentionally excluded to prevent infinite loop

  const handleReturnToPayment = async () => {
    if (!buttonUrl) {
      console.log("🔄 Returning to payment page...");
      setLoading(true);
      // Get pending submission data to determine where to redirect
      const pendingSubmission = sessionStorage.getItem("pendingFormSubmission");

      if (pendingSubmission) {
        const submissionData = JSON.parse(pendingSubmission);

        try {
          const responseData = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/forms/url/${submissionData.customUrl}`
          );

          const form = responseData.data;

          const responseCalculate = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/forms/${form.id}/calculate-amount`,
            { data: submissionData.formData }
          );

          // Calculate amount based on form data and fields
          let calculatedAmount = responseCalculate.data.calculatedAmount;

          if (submissionData.formData && form.fields) {
            form.fields.forEach((field: any) => {
              if (field.type === "select" && field.hasAmount) {
                const selectedValue = submissionData.formData[field.name];
                const option = field.options?.find(
                  (opt: any) => opt.value === selectedValue
                );
                if (option && option.amount) {
                  calculatedAmount += option.amount;
                }
              }
            });
          }

          console.log("Navigating to payment page with state:", {
            formData: submissionData.formData,
            amount: calculatedAmount,
            formTitle: form.title,
            formId: form.id,
          });
          navigate(`/payment/${submissionData.customUrl}`, {
            state: {
              formData: submissionData.formData,
              amount: calculatedAmount,
              formTitle: form.title,
              formId: form.id,
            },
          });
          setLoading(false);
        } catch (error) {
          console.error("Error fetching form data:", error);
          // Fallback then show error message
          setMessage(
            "Failed to retrieve form data. Please go and fill up the form again."
          );
          navigate(`/${submissionData.customUrl}`);
          console.log("Redirecting to form page:", submissionData.customUrl);
          setLoading(false);
        }
      } else {
        setMessage(
          "Failed to retrieve form data. Please go and fill up the form again."
        );
      }
    } else {
      console.log("🔄 Returning to payment page:", buttonUrl);
      navigate(buttonUrl);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        textAlign="center"
      >
        {status === "loading" && (
          <>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom>
              Processing bKash Payment...
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Please wait while we confirm your payment
            </Typography>
          </>
        )}

        {status === "success" && (
          <>
            <Alert severity="success" sx={{ mb: 3, width: "100%" }}>
              <Typography variant="h6" gutterBottom>
                Payment Successful!
              </Typography>
              <Typography variant="body2">{message}</Typography>
            </Alert>
            <Typography variant="body2" color="textSecondary">
              This window will close automatically...
            </Typography>
          </>
        )}

        {status === "error" && (
          <>
            <Alert severity="error" sx={{ mb: 3, width: "100%" }}>
              <Typography variant="h6" gutterBottom>
                Payment Failed
              </Typography>
              <Typography variant="body2">{message}</Typography>
            </Alert>
            <Button
              variant="contained"
              onClick={handleReturnToPayment}
              sx={{ mt: 2 }}
              loading={loading}
            >
              { buttonUrl ? "Retry Payment" : "Return to Payment Page" }
            </Button>
          </>
        )}
      </Box>
    </Container>
  );
};

export default BkashCallbackPage;
